import type { CreditType } from '@monorepo/shared/schemas/common'
import {
  CREDIT_TYPE,
  TOP_UP_METHOD,
  WALLET_STATUS,
} from '@monorepo/shared/schemas/common'
import type { CreateRecurringRule } from '@monorepo/shared/schemas/wallets'
import { format } from 'date-fns'
import {
  Calendar as CalendarIcon,
  Loader2,
  Plus,
  Save,
  Wallet as WalletIcon,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import {
  SubtleTabs,
  SubtleTabsContent,
  SubtleTabsList,
  SubtleTabsTrigger,
} from '@/shared/components/common/tabs/SubtleTabs'
import { Button } from '@/shared/components/ui/button'
import { Calendar } from '@/shared/components/ui/calendar'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Separator } from '@/shared/components/ui/separator'
import { Switch } from '@/shared/components/ui/switch'
import { toast } from '@/shared/lib/toast'
import { cn } from '@/shared/lib/utils'
import {
  useCreateRecurringRule,
  useCreateWallet,
  useCreateWalletAlert,
  useDeleteRecurringRule,
  useRecurringRules,
  useUpdateRecurringRule,
  useUpdateWallet,
  useUpdateWalletAlert,
  useWalletAlerts,
} from '../../hooks/useWallet'
import type { CustomerProfile } from '../../types/customerDetail.types'
import type { CreateWalletRequest, Wallet } from '../../types/wallet.types'

interface WalletCreationDrawerProps {
  customer: CustomerProfile
  mode?: 'create' | 'update'
  existingWallet?: Wallet
  onCreated?: () => void
  onUpdated?: () => void
  trigger?: React.ReactNode
}

export function WalletCreationDrawer({
  customer,
  mode = 'create',
  existingWallet,
  onCreated,
  onUpdated,
  trigger,
}: WalletCreationDrawerProps) {
  const [open, setOpen] = useState(false)
  const isUpdateMode = mode === 'update'

  // Currency options
  const currencies = useMemo(
    () => [
      { code: 'USD', label: 'USD - US Dollar' },
      { code: 'EUR', label: 'EUR - Euro' },
      { code: 'GBP', label: 'GBP - British Pound' },
      { code: 'JPY', label: 'JPY - Japanese Yen' },
      { code: 'CAD', label: 'CAD - Canadian Dollar' },
      { code: 'AUD', label: 'AUD - Australian Dollar' },
    ],
    []
  )

  // Fetch existing recurring rules for update mode first
  const { data: existingRecurringRules = [] } = useRecurringRules(
    existingWallet?.id || '',
    { enabled: isUpdateMode && !!existingWallet?.id }
  )

  const [form, setForm] = useState<
    Omit<CreateWalletRequest, 'customer_id'> & {
      credit_type?: CreditType
      expiry_date?: string
    }
  >({
    currency: existingWallet?.currency || customer.preferred_currency || 'USD',
    conversion_rate: existingWallet?.conversion_rate || 1.0,
    initial_credits: undefined,
    credit_type: CREDIT_TYPE.purchased,
    expiry_date: undefined,
    auto_recharge_enabled: existingWallet?.auto_recharge_enabled || false,
    auto_recharge_threshold:
      existingWallet?.auto_recharge_threshold || undefined,
    auto_recharge_amount: existingWallet?.auto_recharge_amount || undefined,
    status: existingWallet?.status || WALLET_STATUS.active,
  })

  // Recurring rules form state
  const [recurringRuleForm, setRecurringRuleForm] = useState({
    enabled: false,
    ruleId: undefined as string | undefined,
    name: 'Auto Top-up Rule',
    frequency: 'monthly' as 'daily' | 'weekly' | 'monthly',
    amount: undefined as number | undefined,
    startTiming: 'immediately' as 'immediately' | 'custom',
    next_execution: undefined as Date | undefined,
    time: '09:00',
  })
  const [nextExecutionOpen, setNextExecutionOpen] = useState(false)
  const [expiryDateOpen, setExpiryDateOpen] = useState(false)

  // Tab state
  const [activeTab, setActiveTab] = useState('general')

  // Alert settings state
  const [lowBalanceEnabled, setLowBalanceEnabled] = useState(false)
  const [lowBalanceThreshold, setLowBalanceThreshold] = useState('10')

  const createWalletMutation = useCreateWallet()
  const updateWalletMutation = useUpdateWallet()
  const createRecurringRuleMutation = useCreateRecurringRule()
  const updateRecurringRuleMutation = useUpdateRecurringRule()
  const deleteRecurringRuleMutation = useDeleteRecurringRule()
  const createAlertMutation = useCreateWalletAlert()
  const updateAlertMutation = useUpdateWalletAlert()

  // Fetch alerts for update mode
  const { data: alerts } = useWalletAlerts(existingWallet?.id || '', {
    enabled: isUpdateMode && !!existingWallet?.id && open,
  })

  // Memoize derived alert to prevent infinite loop
  // Note: alerts are sorted by updated_at DESC from backend, so find() returns the most recent
  const lowBalanceAlert = useMemo(
    () => alerts?.find(alert => alert.alert_type === 'low_balance'),
    [alerts]
  )

  // Sync alert form state when lowBalanceAlert changes
  useEffect(() => {
    if (isUpdateMode && open) {
      if (lowBalanceAlert) {
        setLowBalanceEnabled(lowBalanceAlert.is_active)
        setLowBalanceThreshold(
          lowBalanceAlert.threshold_amount?.toString() || '10'
        )
      } else {
        setLowBalanceEnabled(false)
        setLowBalanceThreshold('10')
      }
    }
  }, [lowBalanceAlert, isUpdateMode, open])

  const isSaveDisabled =
    !form.currency ||
    (form.conversion_rate !== undefined && form.conversion_rate <= 0) ||
    (!isUpdateMode &&
      form.initial_credits !== undefined &&
      form.initial_credits < 0) ||
    (form.auto_recharge_enabled &&
      (!form.auto_recharge_threshold || !form.auto_recharge_amount)) ||
    (recurringRuleForm.enabled &&
      (!recurringRuleForm.name ||
        !recurringRuleForm.amount ||
        (recurringRuleForm.startTiming === 'custom' &&
          !recurringRuleForm.next_execution))) ||
    createWalletMutation.isPending ||
    createRecurringRuleMutation.isPending ||
    updateWalletMutation.isPending ||
    updateRecurringRuleMutation.isPending ||
    deleteRecurringRuleMutation.isPending

  function updateForm(updates: Partial<typeof form>) {
    setForm(prev => ({
      ...prev,
      ...updates,
    }))
  }

  function updateRecurringRuleForm(updates: Partial<typeof recurringRuleForm>) {
    setRecurringRuleForm(prev => ({
      ...prev,
      ...updates,
    }))
  }

  async function handleSubmit() {
    if (isSaveDisabled) {
      return
    }

    try {
      let wallet: Wallet

      // Map status values between local and shared schema
      const mapStatus = (status?: string) => {
        if (!status) {
          return 'active'
        }
        if (status === 'frozen') {
          return 'suspended'
        }
        if (status === 'terminated') {
          return 'closed'
        }
        return status as 'active' | 'suspended' | 'closed'
      }

      if (isUpdateMode && existingWallet) {
        // Update existing wallet
        const updateData = {
          currency: form.currency || 'USD',
          conversion_rate: form.conversion_rate || 1.0,
          status: mapStatus(form.status),
          auto_recharge_enabled: form.auto_recharge_enabled,
          ...(form.auto_recharge_enabled && {
            auto_recharge_threshold: form.auto_recharge_threshold,
            auto_recharge_amount: form.auto_recharge_amount,
          }),
          ...(!form.auto_recharge_enabled && {
            auto_recharge_threshold: undefined,
            auto_recharge_amount: undefined,
          }),
        }

        const updateResponse = await updateWalletMutation.mutateAsync({
          walletId: existingWallet.id,
          updateData,
        })
        wallet = updateResponse as unknown as Wallet
      } else {
        // Create new wallet
        const walletData = {
          customer_id: customer.id,
          name: `${customer.name}'s Wallet`,
          currency: form.currency || 'USD',
          conversion_rate: form.conversion_rate || 1.0,
          status: mapStatus(form.status),
          auto_recharge_enabled: form.auto_recharge_enabled || false,
          allow_negative_balance: false,
          invoice_requires_payment: true,
          metadata: {},
          // Only add optional fields if they have values
          ...(form.initial_credits !== undefined &&
            form.initial_credits > 0 && {
              initial_credits: form.initial_credits,
            }),
          ...(form.auto_recharge_enabled && {
            ...(form.auto_recharge_threshold !== undefined && {
              auto_recharge_threshold: form.auto_recharge_threshold,
            }),
            ...(form.auto_recharge_amount !== undefined && {
              auto_recharge_amount: form.auto_recharge_amount,
            }),
          }),
        }

        const createResponse =
          await createWalletMutation.mutateAsync(walletData)
        wallet = createResponse as unknown as Wallet
      }

      // Handle recurring rules
      if (recurringRuleForm.enabled && recurringRuleForm.amount) {
        // Combine date and time for start_date
        const nextExecutionDateTime =
          recurringRuleForm.startTiming === 'immediately'
            ? new Date()
            : (() => {
                const date = new Date(recurringRuleForm.next_execution!)
                const [hours, minutes] = (
                  recurringRuleForm.time || '09:00'
                ).split(':')
                date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0)
                return date
              })()

        const recurringRuleData: CreateRecurringRule = {
          wallet_id: wallet.id,
          name: recurringRuleForm.name || 'Auto Top-up Rule',
          status: 'active',
          trigger_type: 'scheduled',
          interval_unit: recurringRuleForm.frequency,
          interval_value: 1,
          time_of_day: `${recurringRuleForm.time || '09:00'}:00`, // Ensure HH:MM:SS format
          timezone: 'UTC',
          top_up_method: TOP_UP_METHOD.fixed_amount,
          fixed_amount: recurringRuleForm.amount,
          start_date: nextExecutionDateTime.toISOString(),
          require_payment_confirmation: false,
          notify_on_execution: false,
          notify_on_failure: true,
          notification_emails: [],
          metadata: {},
        }

        if (isUpdateMode && recurringRuleForm.ruleId) {
          // Update existing recurring rule
          await updateRecurringRuleMutation.mutateAsync({
            walletId: wallet.id,
            ruleId: recurringRuleForm.ruleId,
            ruleData: recurringRuleData,
          })
        } else {
          // Create new recurring rule
          await createRecurringRuleMutation.mutateAsync({
            walletId: wallet.id,
            ruleData: recurringRuleData,
          })
        }
      } else if (
        isUpdateMode &&
        !recurringRuleForm.enabled &&
        recurringRuleForm.ruleId
      ) {
        // Delete existing recurring rule if disabled in update mode
        await deleteRecurringRuleMutation.mutateAsync({
          walletId: wallet.id,
          ruleId: recurringRuleForm.ruleId,
        })
      }

      // Handle alert settings (only in update mode since wallet must exist)
      if (isUpdateMode && existingWallet) {
        const baseAlertData = {
          alert_type: 'low_balance' as const,
          threshold_value: parseFloat(lowBalanceThreshold),
          enabled: lowBalanceEnabled,
          notification_channels: ['email'],
          notification_emails: [],
          cooldown_minutes: 60,
          max_triggers_per_day: 10,
          metadata: {},
          include_balance_info: true,
          include_usage_info: false,
        }

        if (lowBalanceAlert) {
          // Update existing alert
          await updateAlertMutation.mutateAsync({
            walletId: wallet.id,
            alertId: lowBalanceAlert.id,
            alertData: baseAlertData,
          })
        } else if (lowBalanceEnabled) {
          // Create new alert only if enabled
          await createAlertMutation.mutateAsync({
            walletId: wallet.id,
            alertData: baseAlertData,
          })
        }
      }

      setOpen(false)
      if (isUpdateMode) {
        onUpdated?.()
      } else {
        onCreated?.()
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : `Failed to ${isUpdateMode ? 'update' : 'create'} wallet`
      toast.error(errorMessage)
    }
  }

  function resetForm() {
    if (isUpdateMode && existingWallet) {
      // Reset to existing wallet data
      setForm({
        currency: existingWallet.currency,
        conversion_rate: existingWallet.conversion_rate,
        initial_credits: undefined,
        credit_type: CREDIT_TYPE.purchased,
        expiry_date: undefined,
        auto_recharge_enabled: existingWallet.auto_recharge_enabled,
        auto_recharge_threshold: existingWallet.auto_recharge_threshold,
        auto_recharge_amount: existingWallet.auto_recharge_amount,
        status: existingWallet.status,
      })

      // Reset recurring rules form to existing data
      if (existingRecurringRules.length > 0) {
        const existingRule = existingRecurringRules[0]

        // Extract time from time_of_day field (HH:MM:SS format -> HH:MM)
        const extractedTime = existingRule.time_of_day
          ? existingRule.time_of_day.substring(0, 5) // Get HH:MM from HH:MM:SS
          : '09:00'

        // Map interval_unit to frequency
        const mappedFrequency =
          (existingRule.interval_unit as 'daily' | 'weekly' | 'monthly') ||
          'monthly'

        setRecurringRuleForm({
          enabled: existingRule.status === 'active',
          ruleId: existingRule.id,
          name: existingRule.name,
          frequency: mappedFrequency,
          amount: existingRule.fixed_top_up_amount || undefined,
          startTiming: existingRule.next_execution_at
            ? 'custom'
            : 'immediately',
          next_execution: existingRule.next_execution_at
            ? new Date(existingRule.next_execution_at)
            : undefined,
          time: extractedTime,
        })
      } else {
        setRecurringRuleForm({
          enabled: false,
          ruleId: undefined,
          name: 'Auto Top-up Rule',
          frequency: 'monthly',
          amount: undefined,
          startTiming: 'immediately',
          next_execution: undefined,
          time: '09:00',
        })
      }

      // Reset alert state to existing data
      if (lowBalanceAlert) {
        setLowBalanceEnabled(lowBalanceAlert.is_active)
        setLowBalanceThreshold(
          lowBalanceAlert.threshold_amount?.toString() || '10'
        )
      } else {
        setLowBalanceEnabled(false)
        setLowBalanceThreshold('10')
      }
    } else {
      // Reset to default values for create mode
      setForm({
        currency: customer.preferred_currency || 'USD',
        conversion_rate: 1.0,
        initial_credits: undefined,
        credit_type: CREDIT_TYPE.purchased,
        expiry_date: undefined,
        auto_recharge_enabled: false,
        auto_recharge_threshold: undefined,
        auto_recharge_amount: undefined,
        status: WALLET_STATUS.active,
      })
      setRecurringRuleForm({
        enabled: false,
        ruleId: undefined,
        name: 'Auto Top-up Rule',
        frequency: 'monthly',
        amount: undefined,
        startTiming: 'immediately',
        next_execution: undefined,
        time: '09:00',
      })
      setLowBalanceEnabled(false)
      setLowBalanceThreshold('10')
    }
    setActiveTab('general')
  }

  function handleOpenChange(isOpen: boolean) {
    setOpen(isOpen)
    if (isOpen) {
      // Populate form when drawer opens
      resetForm()
    } else {
      // Reset form when drawer closes (cancel)
      resetForm()
    }
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange} direction='right'>
      <DrawerTrigger asChild>
        {trigger ?? (
          <Button type='button' onClick={() => setOpen(true)}>
            <Plus className='w-4 h-4 mr-2' />
            {isUpdateMode ? 'Update Wallet' : 'Create Wallet'}
          </Button>
        )}
      </DrawerTrigger>
      <DrawerContent className='h-screen w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed inset-y-0 right-0'>
        <div className='flex h-full flex-col'>
          <DrawerHeader className='px-6'>
            <DrawerTitle className='flex items-center gap-2'>
              <WalletIcon className='h-5 w-5' />
              {isUpdateMode ? 'Update Wallet' : 'Create Wallet'}
            </DrawerTitle>
            <DrawerDescription>
              {isUpdateMode
                ? `Update wallet settings for ${customer.name}`
                : `Create a new wallet for ${customer.name} to manage credits and billing`}
            </DrawerDescription>
          </DrawerHeader>

          <div className='flex-1 overflow-hidden w-full'>
            <SubtleTabs
              value={activeTab}
              onValueChange={setActiveTab}
              className='h-full flex flex-col'
            >
              <div className='px-6 pt-4'>
                <SubtleTabsList>
                  <SubtleTabsTrigger value='general'>General</SubtleTabsTrigger>
                  {isUpdateMode && (
                    <SubtleTabsTrigger value='alerts'>Alerts</SubtleTabsTrigger>
                  )}
                  <SubtleTabsTrigger value='automation'>
                    Automation
                  </SubtleTabsTrigger>
                </SubtleTabsList>
              </div>

              {/* General Tab */}
              <SubtleTabsContent
                value='general'
                className='flex-1 overflow-y-auto px-6 pb-24'
              >
                <div className='space-y-6 w-full max-w-none'>
                  {/* Customer Info */}
                  <div className='space-y-2'>
                    <Label className='text-sm font-medium'>Customer</Label>
                    <div className='p-3 bg-muted rounded-lg'>
                      <div className='font-medium'>{customer.name}</div>
                      <div className='text-sm text-muted-foreground'>
                        {customer.email}
                      </div>
                      <div className='text-xs text-muted-foreground'>
                        ID: {customer.id}
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Currency Settings */}
                  <div className='space-y-4'>
                    <h4 className='font-medium'>Currency Settings</h4>

                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label htmlFor='currency'>Currency *</Label>
                        <Select
                          value={form.currency || 'USD'}
                          onValueChange={value =>
                            updateForm({ currency: value })
                          }
                        >
                          <SelectTrigger id='currency'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {currencies.map(c => (
                              <SelectItem key={c.code} value={c.code}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className='text-xs text-muted-foreground'>
                          Primary currency for this wallet
                        </div>
                      </div>

                      <div className='space-y-2'>
                        <Label htmlFor='conversion-rate'>Conversion Rate</Label>
                        <Input
                          id='conversion-rate'
                          type='number'
                          step='0.01'
                          min='0.01'
                          value={form.conversion_rate || ''}
                          onChange={e =>
                            updateForm({
                              conversion_rate:
                                parseFloat(e.target.value) || 1.0,
                            })
                          }
                          placeholder='1.0'
                        />
                        <div className='text-xs text-muted-foreground'>
                          Currency to credits conversion rate (e.g., 1 USD = X
                          credits)
                        </div>
                      </div>
                    </div>
                  </div>

                  {!isUpdateMode && (
                    <>
                      <Separator />

                      {/* Initial Setup */}
                      <div className='space-y-4'>
                        <h4 className='font-medium'>Initial Setup</h4>

                        <div className='space-y-4'>
                          <div className='space-y-2'>
                            <Label htmlFor='initial-credits'>
                              Initial Credits
                            </Label>
                            <Input
                              id='initial-credits'
                              type='number'
                              min='0'
                              step='0.01'
                              value={form.initial_credits || ''}
                              onChange={e =>
                                updateForm({
                                  initial_credits: e.target.value
                                    ? parseFloat(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder='0'
                            />
                            <div className='text-xs text-muted-foreground'>
                              Optional: Add credits to the wallet upon creation
                            </div>
                          </div>

                          {form.initial_credits && form.initial_credits > 0 && (
                            <>
                              <div className='space-y-2'>
                                <Label htmlFor='credit-type'>Credit Type</Label>
                                <Select
                                  value={
                                    form.credit_type || CREDIT_TYPE.purchased
                                  }
                                  onValueChange={(value: CreditType) =>
                                    updateForm({ credit_type: value })
                                  }
                                >
                                  <SelectTrigger id='credit-type'>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={CREDIT_TYPE.purchased}>
                                      Purchased
                                    </SelectItem>
                                    <SelectItem value={CREDIT_TYPE.free}>
                                      Free (Promotional)
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                                <div className='text-xs text-muted-foreground'>
                                  Type of credit being added
                                </div>
                              </div>

                              <div className='space-y-2'>
                                <Label htmlFor='expiry-date'>
                                  Expiry Date (Optional)
                                </Label>
                                <Popover
                                  open={expiryDateOpen}
                                  onOpenChange={setExpiryDateOpen}
                                >
                                  <PopoverTrigger asChild>
                                    <Button
                                      id='expiry-date'
                                      variant='outline'
                                      className={cn(
                                        'w-full justify-between font-normal',
                                        !form.expiry_date &&
                                          'text-muted-foreground'
                                      )}
                                    >
                                      {form.expiry_date
                                        ? format(
                                            new Date(form.expiry_date),
                                            'PPP'
                                          )
                                        : 'No expiry date'}
                                      <CalendarIcon className='h-4 w-4' />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    className='w-auto overflow-hidden p-0'
                                    align='start'
                                  >
                                    <Calendar
                                      mode='single'
                                      selected={
                                        form.expiry_date
                                          ? new Date(form.expiry_date)
                                          : undefined
                                      }
                                      captionLayout='dropdown'
                                      onSelect={date => {
                                        if (date) {
                                          updateForm({
                                            expiry_date: date.toISOString(),
                                          })
                                          setExpiryDateOpen(false)
                                        }
                                      }}
                                      disabled={date => date < new Date()}
                                      className='rounded-md border'
                                    />
                                  </PopoverContent>
                                </Popover>
                                <div className='text-xs text-muted-foreground'>
                                  When credits will expire (leave empty for no
                                  expiry)
                                </div>
                                {form.expiry_date && (
                                  <Button
                                    type='button'
                                    variant='ghost'
                                    size='sm'
                                    onClick={() =>
                                      updateForm({ expiry_date: undefined })
                                    }
                                    className='h-6 text-xs'
                                  >
                                    Clear expiry date
                                  </Button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </SubtleTabsContent>

              {/* Alerts Tab (only in update mode) */}
              {isUpdateMode && (
                <SubtleTabsContent
                  value='alerts'
                  className='flex-1 overflow-y-auto px-6 pb-24'
                >
                  <div className='space-y-6 w-full max-w-none'>
                    <Card>
                      <CardHeader className='pb-3'>
                        <CardTitle className='text-sm font-medium'>
                          Low Balance Alert
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4'>
                        <div className='flex items-center justify-between'>
                          <div>
                            <Label
                              htmlFor='low-balance-toggle'
                              className='text-sm font-medium'
                            >
                              Enable Alert
                            </Label>
                            <div className='text-xs text-muted-foreground'>
                              Get notified when wallet balance is low
                            </div>
                          </div>
                          <Switch
                            id='low-balance-toggle'
                            checked={lowBalanceEnabled}
                            onCheckedChange={setLowBalanceEnabled}
                          />
                        </div>

                        {lowBalanceEnabled && (
                          <div className='space-y-2'>
                            <Label htmlFor='alert-threshold'>
                              Alert Threshold
                            </Label>
                            <div className='flex items-center gap-2'>
                              <Input
                                id='alert-threshold'
                                type='number'
                                value={lowBalanceThreshold}
                                onChange={e =>
                                  setLowBalanceThreshold(e.target.value)
                                }
                                placeholder='10'
                                className='flex-1'
                              />
                              <span className='text-sm text-muted-foreground'>
                                credits
                              </span>
                            </div>
                            <div className='text-xs text-muted-foreground'>
                              Alert will trigger when balance falls below this
                              amount
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </SubtleTabsContent>
              )}

              {/* Automation Tab */}
              <SubtleTabsContent
                value='automation'
                className='flex-1 overflow-y-auto px-6 pb-24'
              >
                <div className='space-y-6 w-full max-w-none'>
                  {/* Auto-recharge Settings */}
                  <div className='space-y-4'>
                    <div className='flex items-center justify-between'>
                      <div>
                        <h4 className='font-medium'>Auto-Recharge</h4>
                        <p className='text-sm text-muted-foreground'>
                          Automatically add credits when balance falls below
                          threshold
                        </p>
                      </div>
                      <Switch
                        checked={form.auto_recharge_enabled || false}
                        onCheckedChange={checked =>
                          updateForm({
                            auto_recharge_enabled: checked,
                            auto_recharge_threshold: checked
                              ? form.auto_recharge_threshold
                              : undefined,
                            auto_recharge_amount: checked
                              ? form.auto_recharge_amount
                              : undefined,
                          })
                        }
                      />
                    </div>

                    {form.auto_recharge_enabled && (
                      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4'>
                        <div className='space-y-2'>
                          <Label htmlFor='threshold'>Threshold</Label>
                          <Input
                            id='threshold'
                            type='number'
                            min='0'
                            step='0.01'
                            value={form.auto_recharge_threshold || ''}
                            onChange={e =>
                              updateForm({
                                auto_recharge_threshold:
                                  parseFloat(e.target.value) || undefined,
                              })
                            }
                            placeholder='10.00'
                          />
                          <div className='text-xs text-muted-foreground'>
                            Trigger recharge when balance falls below this
                            amount
                          </div>
                        </div>

                        <div className='space-y-2'>
                          <Label htmlFor='recharge-amount'>
                            Recharge Amount
                          </Label>
                          <Input
                            id='recharge-amount'
                            type='number'
                            min='0.01'
                            step='0.01'
                            value={form.auto_recharge_amount || ''}
                            onChange={e =>
                              updateForm({
                                auto_recharge_amount:
                                  parseFloat(e.target.value) || undefined,
                              })
                            }
                            placeholder='50.00'
                          />
                          <div className='text-xs text-muted-foreground'>
                            Amount to add during auto-recharge
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Recurring Rules Settings */}
                  <div className='space-y-4'>
                    <div className='flex items-center justify-between'>
                      <div>
                        <h4 className='font-medium'>Recurring Credits</h4>
                        <p className='text-sm text-muted-foreground'>
                          Automatically add credits on a scheduled basis
                        </p>
                      </div>
                      <Switch
                        checked={recurringRuleForm.enabled || false}
                        onCheckedChange={checked =>
                          updateRecurringRuleForm({
                            enabled: checked,
                            name: checked
                              ? recurringRuleForm.name
                              : 'Auto Top-up Rule',
                            frequency: checked
                              ? recurringRuleForm.frequency
                              : 'monthly',
                            amount: checked
                              ? recurringRuleForm.amount
                              : undefined,
                            startTiming: checked
                              ? recurringRuleForm.startTiming
                              : 'immediately',
                            next_execution: checked
                              ? recurringRuleForm.next_execution
                              : undefined,
                            time: checked ? recurringRuleForm.time : '09:00',
                          })
                        }
                      />
                    </div>

                    {recurringRuleForm.enabled && (
                      <div className='space-y-4 mt-4'>
                        <div className='space-y-2'>
                          <Label htmlFor='rule-name'>Rule Name</Label>
                          <Input
                            id='rule-name'
                            type='text'
                            value={recurringRuleForm.name || ''}
                            onChange={e =>
                              updateRecurringRuleForm({
                                name: e.target.value || 'Auto Top-up Rule',
                              })
                            }
                            placeholder='Auto Top-up Rule'
                          />
                          <div className='text-xs text-muted-foreground'>
                            Give your recurring rule a descriptive name
                          </div>
                        </div>
                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                          <div className='space-y-2'>
                            <Label htmlFor='frequency'>Frequency</Label>
                            <Select
                              value={recurringRuleForm.frequency}
                              onValueChange={(
                                value: 'daily' | 'weekly' | 'monthly'
                              ) =>
                                updateRecurringRuleForm({ frequency: value })
                              }
                            >
                              <SelectTrigger id='frequency'>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value='daily'>Daily</SelectItem>
                                <SelectItem value='weekly'>Weekly</SelectItem>
                                <SelectItem value='monthly'>Monthly</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className='text-xs text-muted-foreground'>
                              How often to add credits
                            </div>
                          </div>

                          <div className='space-y-2'>
                            <Label htmlFor='recurring-amount'>Amount</Label>
                            <Input
                              id='recurring-amount'
                              type='number'
                              min='0.01'
                              step='0.01'
                              value={recurringRuleForm.amount || ''}
                              onChange={e =>
                                updateRecurringRuleForm({
                                  amount:
                                    parseFloat(e.target.value) || undefined,
                                })
                              }
                              placeholder='25.00'
                            />
                            <div className='text-xs text-muted-foreground'>
                              Credits to add each{' '}
                              {recurringRuleForm.frequency.slice(0, -2)}
                            </div>
                          </div>
                        </div>

                        <div className='space-y-2'>
                          <Label htmlFor='start-timing'>Start Timing</Label>
                          <Select
                            value={recurringRuleForm.startTiming}
                            onValueChange={(value: 'immediately' | 'custom') =>
                              updateRecurringRuleForm({
                                startTiming: value,
                                next_execution:
                                  value === 'immediately'
                                    ? undefined
                                    : recurringRuleForm.next_execution,
                              })
                            }
                          >
                            <SelectTrigger id='start-timing'>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value='immediately'>
                                Start immediately
                              </SelectItem>
                              <SelectItem value='custom'>
                                Custom date/time
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <div className='text-xs text-muted-foreground'>
                            Choose when to start adding recurring credits
                          </div>
                        </div>

                        {recurringRuleForm.startTiming === 'custom' && (
                          <div className='space-y-2'>
                            <Label htmlFor='next-execution'>
                              Select Date & Time
                            </Label>
                            <div className='space-y-2'>
                              <Popover
                                open={nextExecutionOpen}
                                onOpenChange={setNextExecutionOpen}
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    id='next-execution'
                                    variant='outline'
                                    className={cn(
                                      'w-full justify-between font-normal',
                                      !recurringRuleForm.next_execution &&
                                        'text-muted-foreground'
                                    )}
                                  >
                                    {recurringRuleForm.next_execution
                                      ? format(
                                          recurringRuleForm.next_execution,
                                          'PPP'
                                        )
                                      : 'Select date'}
                                    <CalendarIcon className='h-4 w-4' />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className='w-auto overflow-hidden p-0'
                                  align='start'
                                >
                                  <Calendar
                                    mode='single'
                                    selected={recurringRuleForm.next_execution}
                                    captionLayout='dropdown'
                                    onSelect={date => {
                                      if (date) {
                                        updateRecurringRuleForm({
                                          next_execution: date,
                                        })
                                        setNextExecutionOpen(false)
                                      }
                                    }}
                                    disabled={date => date < new Date()}
                                    className='rounded-md border'
                                  />
                                </PopoverContent>
                              </Popover>
                              <div className='space-y-2'>
                                <Label htmlFor='execution-time'>
                                  Time (24-hour format)
                                </Label>
                                <Input
                                  id='execution-time'
                                  type='time'
                                  value={recurringRuleForm.time || '09:00'}
                                  onChange={e =>
                                    updateRecurringRuleForm({
                                      time: e.target.value || '09:00',
                                    })
                                  }
                                  placeholder='HH:MM (24-hour format)'
                                />
                                <div className='text-xs text-muted-foreground'>
                                  Enter time in 24-hour format (e.g., 09:00 for
                                  9 AM, 21:00 for 9 PM)
                                </div>
                              </div>
                            </div>
                            <div className='text-xs text-muted-foreground'>
                              Choose date and time to start the recurring rule
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </SubtleTabsContent>
            </SubtleTabs>
          </div>

          <div className='sticky bottom-0 w-full border-t bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60'>
            <div className='flex items-center justify-between gap-3 px-6 py-4'>
              <Button
                type='button'
                variant='outline'
                onClick={() => setOpen(false)}
              >
                <X className='mr-2 h-4 w-4' /> Cancel
              </Button>
              <Button
                type='button'
                onClick={handleSubmit}
                disabled={isSaveDisabled}
              >
                {createWalletMutation.isPending ||
                createRecurringRuleMutation.isPending ||
                updateWalletMutation.isPending ||
                updateRecurringRuleMutation.isPending ||
                deleteRecurringRuleMutation.isPending ? (
                  <span className='inline-flex items-center gap-2'>
                    <Loader2 className='h-4 w-4 animate-spin' />{' '}
                    {isUpdateMode ? 'Updating...' : 'Creating...'}
                  </span>
                ) : (
                  <span className='inline-flex items-center gap-2'>
                    <Save className='h-4 w-4' />{' '}
                    {isUpdateMode ? 'Update Wallet' : 'Create Wallet'}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
